<template>
  <v-container class="py-10" id="herausforderungenUebersicht">
    <v-row class="align-center">
      <v-col cols="12" lg="6" class="pr-10">
        <div class="subtitle" style="color:#9d0c4a">
          Digitaler Reifegrad
        </div>
        <h2>Model des digitalen Reifegrads</h2>
        <p>
          Der Digital-Report zeigt auf, welche Möglichkeiten sich für NPOs durch
          die Digitalisierung bieten. Unsere Ergebnisse zeigen, dass digital
          reifere Organisationen einen besseren Zugang zu Ressourcen haben.
          Gleichzeitig steht der digitale Reifegrad auch in einem Zusammenhang
          mit bestimmten Organisationsfähigkeiten: Evidenzbasierte Strategie,
          Innovationskraft und Anspruchsgruppenorientierung.
        </p>

        <v-btn
          rounded
          depressed
          color="accent"
          class="ma-2"
          min-width="65%"
          href="https://www.hausdesstiftens.org/wp-content/uploads/Digital-Report-2020-Final.pdf"
          target="blank"
          >Zur Studie</v-btn
        >
        <v-btn
          depressed
          rounded
          color="accent"
          class="ma-2"
          min-width="65%"
          :to="{ name: 'benchmarking' }"
          >Zum Digital-Schnellcheck</v-btn
        >
        <v-btn
          text
          rounded
          depressed
          color="accent"
          class="ma-2"
          min-width="65%"
          v-on:click="showReifegrad = !showReifegrad"
        >
          <span v-if="!showReifegrad"
            ><v-icon>mdi-chevron-down</v-icon>Mehr erfahren</span
          >
          <span v-if="showReifegrad"
            ><v-icon>mdi-chevron-up</v-icon>Einklappen</span
          >
        </v-btn>
      </v-col>
      <v-col cols="12" lg="6">
        <img class="pa-2" src="/images/Abbildung-49.svg" />
      </v-col>
    </v-row>
    <v-container v-if="showReifegrad">
      <h2>Fähigkeiten digital reifer NPOs</h2>
      <p>
        Welche Fähigkeiten braucht es, um eine höhere digitale Reife in einer
        Organisation zu erreichen? Zur Beantwortung diese Frage haben wir uns
        drei Fähigkeiten näher angeschaut, welche bei NPOs in einem
        unmittelbaren Zusammenhang mit dem digitalen Reifegrad stehen könnten:
      </p>

      <ul>
        <li>Evidenzbasierte Strategie</li>
        <li>Innovationskraft</li>
        <li>Anspruchsgruppenorientierung</li>
      </ul>

      <p>
        Beschreibung und Messung dieser Fähigkeiten, können Sie ebenfalls
        unserer <a href="" target="blank"> Studie </a> entnehmen.
      </p>
      <v-row class="align-center">
        <v-col cols="12" md="6">
          <p>
            Die Ergebnisse der Datenanalyse sind in der Abbildung auf der
            rechten Seite illustriert und deuten klar darauf hin. Alle drei von
            uns gemessenen Fähigkeiten weisen einen statistisch signifikanten
            Zusammenhang mit der digitalen Reife von Organisationen auf.
          </p>
          <p>
            Im Konkreten bedeutet das, dass eine Organisation, die sich
            strategische Ziele setzt und evidenzbasierte Entscheidungen trifft,
            sich im digitalen Transformationsprozess schneller und besser
            weiterentwickeln kann als eine Organisation ohne einen solchen
            konkreten Fahrplan. Zudem ist die in der Innovationskraft liegende
            Offenheit für Neues und das Bestreben, innovative Ideen proaktiv
            umzusetzen, ebenfalls eine elementare Eigenschaft für eine
            erfolgreiche Digitalisierung. Durch sie können nötige Veränderungen
            von internen Strukturen und Abläufen sowie die Weiterentwicklung des
            Angebots an die Zielgruppen in die Organisation diffundieren und
            umgesetzt werden. Unsere Ergebnisse zeigen weiterhin, dass die
            Integration von verschiedenen Anspruchsgruppen in die
            Veränderungsprozesse eine wichtige Fähigkeit darstellt, um die
            digitale Transformation effektiv zu durchschreiten. Organisationen,
            die in einem intensiven Dialog mit ihren Anspruchsgruppen stehen und
            diese aktiv einbinden, können nicht nur eine höhere Akzeptanz für
            ihren digitalen Veränderungsprozess in ihrem Umfeld schaffen,
            sondern gleichzeitig auch die Bereitschaft zur Unterstützung sowie
            das Einbringen von Ideen und Wissen von außen in den
            Digitalisierungsprozess fördern.
          </p>
        </v-col>
        <v-col cols="12" md="6">
          <img class="pa-2" src="/images/Abbildung-47.svg" />
        </v-col>
      </v-row>
      <p>
        Neben den hier beschriebenen Fähigkeiten sind zudem die von uns
        beobachteten Charakteristika von digital reiferen NPOs, z.B. in Bezug
        auf die Art und Weise wie sie ihre IT organisieren, in unserer
        <a
          href="https://www.hausdesstiftens.org/wp-content/uploads/Digital-Report-2020-Final.pdf#page=61"
          target="blank"
        >
          Studie
        </a>
        zu finden.
      </p>

      <h2>Ressourcenzugang durch digitale Reife</h2>
      <p>
        An die Frage, welche Fähigkeiten eine digital reife Organisation
        besitzt, schließt sich unmittelbar die Frage an, warum sich eine NPO
        digitalisieren sollte. Die Digitalisierung ist schließlich kein
        Selbstzweck, sondern soll der Organisation und der Gesellschaft, in der
        sie tätig ist, dienen. Zudem erfordert das Durchschreiten der Phasen der
        digitalen Transformation von NPOs eine zusätzliche Bindung von
        personellen und finanziellen Ressourcen. Jedoch haben die bisherigen
        Ergebnisse in diesem Report auch gezeigt, dass neue Möglichkeiten in den
        Bereichen der internen Organisation, der Kommunikation sowie dem Angebot
        an die Zielgruppe geschaffen werden können. Darin liegt die Chance, die
        mit der Digitalisierung verbundenen Anstrengungen angesichts einer
        besseren Erfüllung ihrer sozialen Mission sowie einem erweiterten Zugang
        zu Ressourcen zu rechtfertigen.
      </p>
      <v-row class="align-center">
        <v-col cols="12" md="6">
          <p>
            Zur Untersuchung dieser Annahme haben wir eine tiefergehende Analyse
            durchgeführt. Unser Verständnis von digitaler Reife sowie die
            gewählten Ansätze zur Messung des digitalen Reifegrades der
            Organisationen haben wir hier erklärt. Was aber verstehen wir unter
            dem Zugang zu Ressourcen?
          </p>
          <p>
            Im Non-Profit-Kontext definieren wir, im Einklang mit
            wissenschaftlichen Studien, den Erfolg einer NPO anhand der
            erreichten Wirkung auf die Zielgruppe und Gesellschaft. Um diese
            Wirkung bestmöglich zu realisieren, ist ein guter Zugang zu
            Ressourcen wie Spender, Spenden und/oder Zuschüsse essenziell.
            Zusätzlich zu diesen einzelnen Zielgrößen haben wir die
            Organisationen auch nach ihrer eigenen Einschätzung zur Qualität
            ihres Ressourcenzugangs allgemein befragt. Neben den NPOs, die sich
            primär über Spenden, Mitgliedsbeiträge, gibt es in Deutschland auch
            zunehmend Social Businesses, die zwar im Kern auch eine soziale oder
            ökologische Mission verfolgen, zur Erreichung ihrer Ziele aber
            Produkte und Dienstleistungen vertreiben. Zwischen diesen beiden
            Formen gibt es wiederum auch hybride Organisationen, die beide Wege
            zur Finanzierung nutzen. Um diese Unterschiede abzubilden, haben wir
            Organisationen die sich ganz oder teilweise durch den Vertrieb von
            Produkten oder Dienstleistungen finanzieren nach ihrem Umsatz oder
            Gewinn gefragt.
          </p>
        </v-col>
        <v-col cols="12" md="6">
          <img class="pa-2" src="/images/Abbildung-48.svg" />
        </v-col>
      </v-row>
      <p>
        Die Ergebnisse der Analyse zeigen ein eindeutiges Bild: Der digitale
        Reifegrad weist einen positiven Zusammenhang mit allen einzelnen
        Ressourcenarten (Spenden, Spender, Mitglieder, Umsatz, Gewinn) sowie der
        subjektiv wahrgenommenen Qualität des Ressourcenzugangs im Allgemeinen
        auf. Demnach kann die Digitalisierung für NPOs neue Möglichkeiten
        schaffen, zusätzliche Ressourcen zu akquirieren. Die Zusammenführung der
        Erkenntnisse aus den beiden Analysen machen deutlich, dass die
        Fähigkeiten von Organisationen in Zusammenhang mit dem digitalen
        Reifegrad stehen und der digitale Reifegrad wiederum in Zusammenhang mit
        dem Zugang zu Ressourcen steht.
      </p>
      <v-btn
        text
        rounded
        depressed
        color="accent"
        class="ma-2 d-block"
        v-on:click="showReifegrad = false"
        ><v-icon>mdi-chevron-up</v-icon>Einklappen</v-btn
      >
    </v-container>
  </v-container>
</template>

<script>
export default {
  name: "Reifegrad",
  data: () => ({
    showReifegrad: false
  })
};
</script>
